<script lang="ts">
  import { onMount } from 'svelte';
  import { planter, lysFamilier } from '../lib/stores';
  import { overlayOpened } from '../lib/overlayBack';
  import { handterDialogTastatur, startDialogFokus } from '../lib/dialogFokus';
  import type { Plante, LysFamilieId, PlanteKategori } from '../lib/database.types';
  import { vurderLysKompatibilitet, vurderVannKompatibilitet, vekeEgnetFarge } from '../lib/lys';

  let {
    plassLabel,
    eksisterendePlanter,
    onLukk,
    onValgt,
  }: {
    /** Visningsnavn for plassen, f.eks. "Potte 1 · foran". */
    plassLabel: string;
    eksisterendePlanter: Plante[];
    onLukk: () => void;
    onValgt: (planteId: string) => void;
  } = $props();

  let sok = $state('');
  let bildeFeilet = $state<Record<string, boolean>>({});
  let kategoriFilter = $state<PlanteKategori | 'alle'>('alle');
  // svelte-ignore state_referenced_locally
  let familieFilter = $state<LysFamilieId | 'alle' | 'kompatible'>(
    eksisterendePlanter.length > 0 ? 'kompatible' : 'alle',
  );
  let dialogEl: HTMLDivElement;

  const kategorier: { id: PlanteKategori | 'alle'; navn: string; emoji: string }[] = [
    { id: 'alle', navn: 'Alle', emoji: '🌿' },
    { id: 'urt', navn: 'Urter', emoji: '🌱' },
    { id: 'salat', navn: 'Salat', emoji: '🥬' },
    { id: 'frukt', navn: 'Frukt', emoji: '🍅' },
    { id: 'gronnsak', navn: 'Grønnsak', emoji: '🥒' },
    { id: 'blomst', navn: 'Blomst', emoji: '🌺' },
    { id: 'mikrogront', navn: 'Mikrogrønt', emoji: '🌾' },
  ];

  // Hvilke familier er kompatible med eksisterende planter?
  const kompatibleFamilier = $derived.by(() => {
    if (eksisterendePlanter.length === 0) return new Set<LysFamilieId>();
    const familier = new Set<LysFamilieId>();
    for (const f of $lysFamilier) {
      const test = [...eksisterendePlanter, { lys_familie: f.id } as Plante];
      const rap = vurderLysKompatibilitet(test);
      if (rap.niva === 'perfekt' || rap.niva === 'god') familier.add(f.id);
    }
    return familier;
  });

  const filtrert = $derived.by(() => {
    let liste = $planter.filter((p) => p.publisert);
    if (kategoriFilter !== 'alle') liste = liste.filter((p) => p.kategori === kategoriFilter);
    if (familieFilter === 'kompatible' && kompatibleFamilier.size > 0) {
      liste = liste.filter((p) => kompatibleFamilier.has(p.lys_familie));
    } else if (familieFilter !== 'alle' && familieFilter !== 'kompatible') {
      liste = liste.filter((p) => p.lys_familie === familieFilter);
    }
    if (sok.trim()) {
      const q = sok.toLowerCase().trim();
      liste = liste.filter(
        (p) =>
          p.navn.toLowerCase().includes(q) ||
          (p.vitenskapelig?.toLowerCase().includes(q) ?? false) ||
          (p.varianter?.some((v) => v.toLowerCase().includes(q)) ?? false),
      );
    }
    return liste;
  });

  // Fokus følger samme inn-/retur-mønster som det gjenbrukbare arket.
  onMount(() => {
    const gjenopprettFokus = startDialogFokus(dialogEl);
    const avregistrerTilbake = overlayOpened(onLukk);
    return () => {
      avregistrerTilbake();
      gjenopprettFokus();
    };
  });

  function previewKompatibilitet(p: Plante) {
    const test = [...eksisterendePlanter, p];
    return vurderLysKompatibilitet(test);
  }

  /**
   * Vann-konflikt FØR man velger: «lav»-plante i kasse med «høy»-planter (eller
   * omvendt) er dømt til å mistrives i felles reservoar — det skal synes i
   * lista, ikke først i lys-arket etterpå. Kun harde konflikter flagges her;
   * veke-egnethet har alt sin egen badge per plante.
   */
  function previewVannKonflikt(p: Plante): boolean {
    if (eksisterendePlanter.length === 0) return false;
    return vurderVannKompatibilitet([...eksisterendePlanter, p]).niva === 'inkompatibel';
  }
</script>

<div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fade-in">
  <button
    class="absolute inset-0 bg-black/60 backdrop-blur-sm"
    aria-label="Lukk"
    onclick={onLukk}
  ></button>

  <div
    class="relative w-full max-w-3xl bg-surface border border-border rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92vh]"
    role="dialog"
    aria-modal="true"
    aria-labelledby="velger-tittel"
    tabindex="-1"
    bind:this={dialogEl}
    onkeydown={(e) => handterDialogTastatur(e, dialogEl, onLukk)}
  >
    <!-- Header -->
    <div class="p-5 border-b border-border flex items-center gap-4">
      <div class="flex-1">
        <h2 id="velger-tittel" class="font-display text-lg font-semibold">Velg plante til {plassLabel}</h2>
        <p class="text-text-muted text-xs mt-0.5">
          {filtrert.length} planter passer dine valg
        </p>
      </div>
      <button class="btn-ghost !px-2 !py-2" onclick={onLukk} aria-label="Lukk">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>

    <!-- Filter -->
    <div class="p-5 border-b border-border space-y-3">
      <input
        type="search"
        placeholder="Søk… (f.eks. «basilikum» eller «tomat»)"
        aria-label="Søk etter plante"
        bind:value={sok}
        class="input"
      />

      <!-- Kategorier -->
      <div class="flex gap-1.5 flex-wrap">
        {#each kategorier as k}
          <button
            class="chip border-border transition-colors {kategoriFilter === k.id
              ? 'bg-leaf/15 text-leaf border-leaf/30'
              : 'bg-surface-raised text-text-muted hover:bg-surface-hover'}"
            onclick={() => (kategoriFilter = k.id)}
            aria-pressed={kategoriFilter === k.id}
          >
            <span>{k.emoji}</span>
            <span>{k.navn}</span>
          </button>
        {/each}
      </div>

      <!-- Lys-familier -->
      <div class="flex gap-1.5 flex-wrap items-center">
        <span class="text-xs text-text-dim mr-1">Lysbehov:</span>
        {#if eksisterendePlanter.length > 0}
          <button
            class="chip border-border transition-colors {familieFilter === 'kompatible'
              ? 'bg-leaf/15 text-leaf border-leaf/30'
              : 'bg-surface-raised text-text-muted hover:bg-surface-hover'}"
            onclick={() => (familieFilter = 'kompatible')}
            aria-pressed={familieFilter === 'kompatible'}
          >
            ✓ Kompatible
          </button>
        {/if}
        <button
          class="chip border-border transition-colors {familieFilter === 'alle'
            ? 'bg-surface-hover text-text border-border-strong'
            : 'bg-surface-raised text-text-muted hover:bg-surface-hover'}"
          onclick={() => (familieFilter = 'alle')}
          aria-pressed={familieFilter === 'alle'}
        >
          Alle
        </button>
        {#each $lysFamilier as f (f.id)}
          <button
            class="chip border-border transition-colors {familieFilter === f.id
              ? 'bg-surface-hover text-text border-border-strong'
              : 'bg-surface-raised text-text-muted hover:bg-surface-hover'}"
            onclick={() => (familieFilter = f.id)}
            aria-pressed={familieFilter === f.id}
          >
            <span>{f.emoji}</span>
            <span>{f.navn.split(' ')[0]}</span>
          </button>
        {/each}
      </div>
    </div>

    <!-- Liste -->
    <div class="flex-1 overflow-y-auto p-3 sm:p-5">
      {#if filtrert.length === 0}
        <div class="text-center py-12 text-text-muted text-sm">
          Ingen planter matcher disse filtrene — prøv «Alle» under Lysbehov eller et annet søkeord.
        </div>
      {:else}
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {#each filtrert as p (p.id)}
            {@const komp = eksisterendePlanter.length > 0 ? previewKompatibilitet(p) : null}
            {@const vannKonflikt = previewVannKonflikt(p)}
            <button
              class="card-raised p-3 text-left hover:bg-surface-hover hover:border-border-strong transition-colors flex items-start gap-3"
              onclick={() => onValgt(p.id)}
            >
              {#if p.bilde_url && !bildeFeilet[p.id]}
                <img
                  src={p.bilde_url}
                  alt={p.navn}
                  loading="lazy"
                  onerror={() => (bildeFeilet[p.id] = true)}
                  class="w-11 h-11 rounded-lg object-cover shrink-0 bg-surface-raised"
                />
              {:else}
                <span class="text-2xl">{p.emoji ?? '🌿'}</span>
              {/if}
              <div class="flex-1 min-w-0">
                <div class="font-medium leading-tight">{p.navn}</div>
                {#if p.vitenskapelig}
                  <div class="text-xs italic text-text-dim mt-0.5 truncate">{p.vitenskapelig}</div>
                {/if}
                <div class="mt-1.5 flex flex-wrap gap-1.5 items-center">
                  <span class="text-[10px] {vekeEgnetFarge(p.veke_egnet)}">
                    {p.veke_egnet === 'utmerket'
                      ? '✓ Veke'
                      : p.veke_egnet === 'bra'
                        ? '✓ Veke OK'
                        : p.veke_egnet === 'forsiktig'
                          ? '⚠ Veke risiko'
                          : '✕ Ikke veke'}
                  </span>
                  {#if p.dli_optimal}
                    <span class="text-[10px] text-text-dim">DLI {p.dli_optimal}</span>
                  {/if}
                  {#if p.dager_til_hosting}
                    <span class="text-[10px] text-text-dim">{p.dager_til_hosting}d</span>
                  {/if}
                  {#if komp && komp.niva !== 'perfekt'}
                    <span
                      class="text-[10px] {komp.niva === 'god'
                        ? 'text-leaf'
                        : komp.niva === 'risikabel'
                          ? 'text-sun'
                          : 'text-rose'}"
                    >
                      <!-- Si hva det gjelder: «✓ god» alene var uklart. -->
                      {komp.niva === 'god'
                        ? '✓ lys: nabo-familie'
                        : komp.niva === 'risikabel'
                          ? '⚠ lys: stort kompromiss'
                          : '✕ lys: passer ikke'}
                    </span>
                  {/if}
                  {#if vannKonflikt}
                    <span class="text-[10px] text-rose" title="For ulikt vannbehov for samme veke-reservoar som de eksisterende plantene">
                      ✕ ulikt vannbehov
                    </span>
                  {/if}
                </div>
              </div>
            </button>
          {/each}
        </div>
      {/if}
    </div>
  </div>
</div>
